import { T, useLocale } from "../localization";
import React, { useEffect, useRef, useState } from "react";
import { FiPlay, FiPause, FiRotateCcw, FiRotateCw, FiSkipBack, FiSkipForward, FiBookmark, FiTrash2, FiX } from "react-icons/fi";
import { requestAudiobookAccess, saveAudiobookProgress } from "../api/rest-apis";
import { ListeningAnalytics } from "../helpers/listeningAnalytics";

const Play: any = FiPlay, Pause: any = FiPause, Back: any = FiRotateCcw, Forward: any = FiRotateCw;
const Previous: any = FiSkipBack, Next: any = FiSkipForward, Bookmark: any = FiBookmark, Trash: any = FiTrash2, Close: any = FiX;
export const audioTime = (value: number) => {
  const seconds = Math.max(0, Math.floor(Number.isFinite(value) ? value : 0));
  const minutes = Math.floor(seconds / 60);
  return `${minutes >= 60 ? `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, "0")}` : minutes}:${String(seconds % 60).padStart(2, "0")}`;
};
type Mark = { id: string; time: number; label: string };
type Props = { book: any; access: any; onClose: () => void; onProgress: (progress: any) => void; onLimit: (message: string) => void };

export default function AudiobookPlayer({ book, access: initialAccess, onClose, onProgress, onLimit }: Props) {
  const { t, language } = useLocale();
  const [access, setAccess] = useState(initialAccess);
  const [listeningTracker] = useState(() => new ListeningAnalytics({ ...initialAccess.analytics, book_id: book.id, book_title: book.title, access_mode: initialAccess.access_mode }));
  const listening = useRef(listeningTracker);
  listening.current.updateContext({ ...initialAccess.analytics, book_id: book.id, book_title: book.title, access_mode: access.access_mode });
  const audioRef = useRef<HTMLAudioElement>(null);
  const mountedAudio = useRef<HTMLAudioElement | null>(null);
  const resumeAt = useRef(Number(access.progress?.position_seconds) || 0);
  const renewing = useRef(false);
  const renewed = useRef(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [syncStatus, setSyncStatus] = useState("");
  const [rate, setRate] = useState(Number(access.progress?.playback_rate) || 1);
  const [marks, setMarks] = useState<Mark[]>(access.progress?.bookmarks || []);
  const [sleep, setSleep] = useState("0");
  const [sleepRemaining, setSleepRemaining] = useState(0);
  const [sleepMessage, setSleepMessage] = useState("");
  const sleepDeadline = useRef(0);
  const sleepChapterEnd = useRef(0);
  const lastSave = useRef(0);
  const ready = useRef(false);
  const limitShown = useRef(false);
  const saveQueue = useRef(Promise.resolve());
  const latest = useRef({ rate, onProgress });
  latest.current = { rate, onProgress };
  const chapters: { title: string; start: number }[] = (access.chapters || []).filter((chapter: any) => Number.isFinite(Number(chapter.start))).map((chapter: any) => ({ title: chapter.title, start: Number(chapter.start) })).sort((a: any, b: any) => a.start - b.start);
  const maximum = access.max_seconds > 0 ? Number(access.max_seconds) : Infinity;
  const playableDuration = Math.min(duration || Number(access.duration_seconds) || 0, maximum);
  const activeChapter = Math.max(0, chapters.reduce((result, chapter, index) => chapter.start <= position ? index : result, 0));

  const save = (force = false, overrides: any = {}) => {
    const audio = audioRef.current || mountedAudio.current;
    if (!audio || !ready.current || (!force && Date.now() - lastSave.current < 10000)) return;
    lastSave.current = Date.now();
    const payload = {
      position_seconds: Math.max(0, audio.currentTime || 0),
      duration_seconds: Number.isFinite(audio.duration) ? audio.duration : Number(access.duration_seconds) || 0,
      ...overrides,
    };
    // Serialize writes so a slower earlier request cannot replace a newer position.
    saveQueue.current = saveQueue.current.catch(() => {}).then(async () => {
      try {
        const result = await saveAudiobookProgress(book.id, payload);
        latest.current.onProgress(result.progress);
        if (overrides.bookmark_add || overrides.bookmark_remove) setMarks(result.progress.bookmarks || []);
        setSyncStatus("Listening position saved");
      } catch {
        setSyncStatus("Position could not sync. Check your connection.");
      }
    });
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  const showLimit = () => {
    audioRef.current?.pause();
    if (!limitShown.current) {
      limitShown.current = true;
      if (audioRef.current) listening.current.event("audiobook_limit_reached", audioRef.current);
      onLimit("Become a premium member to listen to the full audiobook.");
    }
  };
  const seek = (target: number) => {
    const audio = audioRef.current;
    if (!audio || !ready.current) return;
    if (access.access_mode !== "premium" && target >= maximum) { showLimit(); return; }
    listening.current.sample(audio);
    audio.currentTime = Math.max(0, Math.min(target, playableDuration || target));
    listening.current.reset(audio);
    listening.current.event("audiobook_seek", audio);
    setPosition(audio.currentTime);
    save(true);
  };
  const play = async () => {
    if (!audioRef.current) return;
    setError("");
    try { await audioRef.current.play(); }
    catch (failure: any) {
      listening.current.event("audiobook_playback_error", audioRef.current!, { reason: failure?.name === "NotAllowedError" ? "autoplay_blocked" : "play_failed" });
      setLoading(false);
      setError(failure?.name === "NotAllowedError" ? "Press Play to begin listening." : "Playback failed. Reopen the audiobook to renew its audio link.");
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    mountedAudio.current = audio;
    audio.src = access.stream_url;
    audio.load();
    const pageHide = () => { saveRef.current(true); listening.current.sample(audio); listening.current.flush(audio); };
    const visibility = () => { if (document.hidden) pageHide(); };
    window.addEventListener("pagehide", pageHide);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      saveRef.current(true);
      listening.current.stop(audio, "audiobook_closed");
      audio.pause();
      ready.current = false;
      audio.removeAttribute("src");
      audio.load();
      window.removeEventListener("pagehide", pageHide);
      document.removeEventListener("visibilitychange", visibility);
    };
  // Each access request mounts a new player, after its audio element exists.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sleep === "0" || sleep === "chapter") return;
    const timer = window.setInterval(() => {
      const remaining = Math.max(0, sleepDeadline.current - Date.now());
      setSleepRemaining(Math.ceil(remaining / 1000));
      if (remaining === 0) {
        audioRef.current?.pause();
        saveRef.current(true);
        setSleep("0");
        setSleepMessage("Sleep timer stopped playback.");
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [sleep]);

  const addBookmark = () => {
    if (audioRef.current) listening.current.event("audiobook_bookmark_add", audioRef.current);
    const mark = { id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, time: position, label: `Bookmark at ${audioTime(position)}` };
    save(true, { bookmark_add: mark });
  };
  const removeBookmark = (id: string) => { if (audioRef.current) listening.current.event("audiobook_bookmark_remove", audioRef.current); save(true, { bookmark_remove: id }); };
  const setTimer = (value: string) => {
    if (audioRef.current) listening.current.event("audiobook_sleep_timer", audioRef.current, { timer: value });
    setSleep(value); setSleepMessage("");
    sleepDeadline.current = value !== "chapter" ? Date.now() + Number(value) * 60000 : 0;
    setSleepRemaining(Number(value) * 60 || 0);
    sleepChapterEnd.current = chapters[activeChapter + 1]?.start || playableDuration;
  };

  return <aside className="audiobook-player open" aria-label={t("Audiobook player")}>
    <div className="player-book"><img src={book.cover_url} alt="" /><div><span><T text={playing ? "Now playing" : "Audiobook"} /></span><strong>{book.title}</strong><small><T text={access.notice} />{chapters.length ? ` · ${t(chapters[activeChapter]?.title)}` : ""}</small></div><button title={t("Close player")} aria-label={t("Close player")} onClick={onClose}><Close /></button></div>
    <audio ref={audioRef} preload="metadata"
      onLoadedMetadata={() => {
        const audio = audioRef.current!;
        ready.current = true; setLoading(false); setDuration(Number.isFinite(audio.duration) ? audio.duration : Number(access.duration_seconds) || 0);
        const saved = resumeAt.current;
        const end = Math.min(Number.isFinite(audio.duration) ? audio.duration : Infinity, maximum);
        audio.currentTime = saved > 0 && saved < end - 1 ? saved : 0;
        listening.current.reset(audio);
        audio.playbackRate = latest.current.rate; setPosition(audio.currentTime); void play();
      }}
      onDurationChange={() => { const value = audioRef.current?.duration; if (value && Number.isFinite(value)) setDuration(value); }}
      onTimeUpdate={() => {
        const audio = audioRef.current!; setPosition(audio.currentTime);
        listening.current.sample(audio);
        if (sleepDeadline.current > 0 && sleep !== "0" && sleep !== "chapter" && Date.now() >= sleepDeadline.current) { audio.pause(); setSleep("0"); setSleepMessage("Sleep timer stopped playback."); }
        if (access.access_mode !== "premium" && audio.currentTime >= maximum - 0.1) showLimit();
        if (sleep === "chapter" && sleepChapterEnd.current > 0 && audio.currentTime >= sleepChapterEnd.current) { audio.pause(); setSleep("0"); setSleepMessage("Sleep timer stopped playback."); }
        save();
      }}
      onPlay={() => { setPlaying(true); setLoading(false); limitShown.current = false; }}
      onPlaying={() => { listening.current.start(audioRef.current!); setLoading(false); renewed.current = false; }}
      onPause={() => { listening.current.stop(audioRef.current!, "audiobook_paused"); setPlaying(false); save(true); }}
      onSeeking={() => listening.current.reset(audioRef.current!)}
      onSeeked={() => { listening.current.reset(audioRef.current!); save(true); }}
      onRateChange={() => { listening.current.reset(audioRef.current!); listening.current.event("audiobook_speed_change", audioRef.current!); }}
      onWaiting={() => { listening.current.stop(audioRef.current!, "audiobook_buffering"); setLoading(true); }} onCanPlay={() => setLoading(false)}
      onEnded={() => { listening.current.stop(audioRef.current!); listening.current.event("audiobook_segment_complete", audioRef.current!); setPlaying(false); save(true); if (access.access_mode !== "premium") showLimit(); if (sleep === "chapter") setTimer("0"); }}
      onError={async () => {
        if (renewing.current) return;
        listening.current.stop(audioRef.current!);
        listening.current.event("audiobook_playback_error", audioRef.current!, { reason: "media_error" });
        if (renewed.current) { setLoading(false); setPlaying(false); setError("The audio could not be loaded. Reopen the audiobook to try again."); return; }
        renewing.current = true; renewed.current = true;
        try {
          const audio = audioRef.current!;
          resumeAt.current = audio.currentTime || resumeAt.current;
          const renewedAccess = await requestAudiobookAccess(book.id);
          if (!audioRef.current) return;
          if (!renewedAccess.stream_url) throw new Error("Missing audio stream");
          setAccess(renewedAccess);
          audio.src = renewedAccess.stream_url;
          audio.load();
        } catch { setLoading(false); setPlaying(false); setError("The audio could not be loaded. Reopen the audiobook to try again."); }
        finally { renewing.current = false; }
      }} />
    <div className="audiobook-seek"><span>{audioTime(position)}</span><input aria-label={t("Listening position")} type="range" min="0" max={playableDuration || 1} step="1" value={Math.min(position, playableDuration || 0)} disabled={!ready.current} onChange={event => seek(Number(event.target.value))} /><span>{audioTime(playableDuration)}</span></div>
    <div className="audiobook-controls">
      <div className="audiobook-transport">
        <button title={t("Previous chapter")} aria-label={t("Previous chapter")} disabled={!chapters.length || !ready.current} onClick={() => seek(chapters[Math.max(0, activeChapter - 1)].start)}><Previous /></button>
        <button title={t("Back 30 seconds")} aria-label={t("Back 30 seconds")} disabled={!ready.current} onClick={() => seek(position - 30)}><Back /><small>30</small></button>
        <button className="audiobook-toggle" title={t(playing ? "Pause" : "Play")} aria-label={t(playing ? "Pause" : "Play")} onClick={() => playing ? audioRef.current?.pause() : void play()}>{playing ? <Pause /> : <Play />}</button>
        <button title={t("Forward 30 seconds")} aria-label={t("Forward 30 seconds")} disabled={!ready.current} onClick={() => seek(position + 30)}><Forward /><small>30</small></button>
        <button title={t("Next chapter")} aria-label={t("Next chapter")} disabled={!chapters[activeChapter + 1] || chapters[activeChapter + 1]?.start >= maximum} onClick={() => seek(chapters[activeChapter + 1].start)}><Next /></button>
      </div>
      <label><T text="Speed" /><select aria-label={t("Playback speed")} value={rate} onChange={event => { const value = Number(event.target.value); setRate(value); latest.current.rate = value; if (audioRef.current) audioRef.current.playbackRate = value; save(true, { playback_rate: value }); }}>{[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3].map(value => <option key={value} value={value}>{value.toLocaleString(language === "de" ? "de-DE" : "en-US")}x</option>)}</select></label>
      <label><T text="Sleep timer" /><select aria-label={t("Sleep timer")} value={sleep} onChange={event => setTimer(event.target.value)}><option value="0"><T text="Off" /></option>{[5, 10, 15, 30, 45, 60, 90].map(value => <option key={value} value={value}>{value} <T text="minutes" /></option>)}<option value="chapter" disabled={!playableDuration}><T text="End of" /> <T text={chapters.length ? "chapter" : "book"} /></option></select></label>
      <button className="audiobook-add-bookmark" disabled={!ready.current || marks.length >= 200} onClick={addBookmark}><Bookmark /> <T text="Bookmark" /></button>
    </div>
    {chapters.length > 0 && <label className="audiobook-chapter-picker"><T text="Chapter" /><select aria-label={t("Chapter")} value={activeChapter} onChange={event => seek(chapters[Number(event.target.value)].start)}>{chapters.map((chapter, index) => <option key={index} value={index} disabled={chapter.start >= maximum}><T text={chapter.title} /> · {audioTime(chapter.start)}</option>)}</select></label>}
    <div className="audiobook-player-status" role="status"><T text={error || (loading ? "Loading audio..." : syncStatus)} />{sleep !== "0" && <span><T text="Sleep:" /> {sleep === "chapter" ? <T text={chapters.length ? "end of chapter" : "end of book"} /> : audioTime(sleepRemaining)}</span>}{sleepMessage && <span><T text={sleepMessage} /></span>}</div>
    <details className="audiobook-bookmarks"><summary><T text="Bookmarks (" />{marks.length})</summary><div>{!marks.length && <p><T text="No bookmarks yet." /></p>}{marks.map(mark => <div className="audiobook-bookmark-row" key={mark.id}><button onClick={() => seek(mark.time)}><T text={mark.label} /></button><button title={t("Delete bookmark")} aria-label={`${t("Delete")} ${t(mark.label)}`} onClick={() => removeBookmark(mark.id)}><Trash /></button></div>)}</div></details>
  </aside>;
}
